import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { format, eachDayOfInterval, startOfMonth, endOfMonth, getMonth, getYear } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const API_BASE_URL = "https://api.muktihospital.com/api";

const DoctorPerformanceChart = ({ user }) => {
    const [chartData, setChartData] = useState({ series: [], options: {} });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.email) {
            setLoading(false);
            console.log("Chart: Waiting for user email...");
            return;
        }

        const fetchChartData = async () => {
            setLoading(true);
            const now = new Date();
            const year = getYear(now);
            const month = getMonth(now);
            const firstDay = startOfMonth(now);
            const lastDay = endOfMonth(now);
            const daysOfMonth = eachDayOfInterval({ start: firstDay, end: lastDay });

            const labels = daysOfMonth.map(day => format(day, 'd'));
            const dailyPatients = Array(daysOfMonth.length).fill(0);
            const dailyEarnings = Array(daysOfMonth.length).fill(0);

            try {
                const api = axios.create({ baseURL: API_BASE_URL, headers: { "x-api-key": API_KEY } });

                // 1. Fetch both doctors and appointments
                const [doctorsRes, appointmentsRes] = await Promise.all([
                    api.get(`/doctor`),
                    api.get(`/appointment`)
                ]);

                const allDoctors = doctorsRes.data?.data || doctorsRes.data || [];
                const allAppointments = appointmentsRes.data?.appointments || [];

                const myDoctorProfile = allDoctors.find(
                    doc => doc.email === user.email
                );

                // If no profile is found, stop
                if (!myDoctorProfile) {
                    console.error("Could not find doctor profile for email:", user.email);
                    setLoading(false);
                    return;
                }
                
                const loggedInDoctorId = myDoctorProfile.id;

                // 3. Filter appointments by the correct doctor ID
                const myAppointments = allAppointments.filter(app => app.doctorId === loggedInDoctorId);
                
                // 4. Filter for *this month* and *approved*
                const myApprovedAppointmentsThisMonth = myAppointments.filter(app => {
                    if (!app.appointmentDate || app.status !== 'approved') return false;
                    const appDate = new Date(app.appointmentDate);
                    return appDate.getFullYear() === year && appDate.getMonth() === month;
                });

                // 5. Process the data for each day of the month
                daysOfMonth.forEach((day, index) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    
                    const appointmentsForDay = myApprovedAppointmentsThisMonth.filter(app => 
                        app.appointmentDate && app.appointmentDate.startsWith(dateStr)
                    );
                    
                    dailyPatients[index] = appointmentsForDay.length;
                    dailyEarnings[index] = appointmentsForDay.reduce((sum, app) => sum + (app.consultationFee || 0), 0);
                });
                
                // 6. Set the final chart data
                setChartData({
                    series: [
                        { name: 'Approved Patients', type: 'column', data: dailyPatients },
                        { name: 'Earnings', type: 'line', data: dailyEarnings }
                    ],
                    options: {
                        chart: { height: 400, type: 'line', toolbar: { show: false } },
                        stroke: { width: [0, 4], curve: 'smooth' },
                        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
                        dataLabels: { enabled: false },
                        labels: labels,
                        yaxis: [
                            {
                                title: { text: 'Number of Patients' },
                                labels: { formatter: (val) => Math.round(val) }
                            },
                            {
                                opposite: true,
                                title: { text: 'Total Earnings (৳)' },
                                labels: { formatter: (val) => `৳${(val / 1000).toFixed(0)}k` }
                            }
                        ],
                        colors: ["#6AC75A", "#465DFF"],
                        tooltip: { 
                            y: { 
                                formatter: (val, { seriesIndex }) => {
                                    if (seriesIndex === 0) {
                                        return `${Math.round(val)} patients`;
                                    } else { 
                                        return `৳${Math.round(val).toLocaleString()}`;
                                    }
                                }
                            } 
                        }
                    },
                });

            } catch (error) {
                console.error("Error fetching chart data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, [user]);

    if (loading) {
        return <Skeleton height={400} />;
    }

    return (
        <div>
            <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={400}
            />
        </div>
    );
};

export default DoctorPerformanceChart;